import type { ReactNode } from 'react';
import type { I18nProviderProps } from './const';
import { useEffect } from 'react';
import intlInstance, { changeCurrentLocale } from 'react-intl-universal';
import { I18nContext } from './context';

let init = false;

/**
 * I18nProvider - 国际化 Provider
 *
 * 职责：
 * 1. 初始化 intl（react-intl-universal）
 * 2. 提供 I18nContext（locale + setLocale + fallbackLocale）
 * 3. 监听语言切换，先占位再切换
 *
 * 策略：
 * - changeCurrentLocale 要求目标语言文案已存在
 * - 切换语言时先加载空对象占位，再调用 changeCurrentLocale
 * - Consumer 后续会加载真实文案覆盖占位
 */
export function I18nProvider(props: I18nProviderProps): ReactNode {
  const { children, locale, onLocaleChange, fallbackLocale, debug } = props;

  // 初始化 intl（只执行一次）
  useEffect(() => {
    if (debug) {
      // @ts-expect-error debug
      window.__intl__ = intlInstance;
    }

    if (init) {
      return;
    }

    init = true;

    const locales = {};
    locales[locale] = {};
    locales[fallbackLocale] = {};

    intlInstance.init({
      currentLocale: locale,
      fallbackLocale,
      locales,
      warningHandler: () => {},
    });
  }, []);

  // 切换语言回调
  const setLocale = (newLocale: string) => {
    onLocaleChange?.(newLocale);
  };

  useEffect(() => {
    if (locale) {
      // 1. 先给目标语言加载空对象占位（如果不存在）
      if (!intlInstance.getInitOptions()?.locales?.[locale]) {
        intlInstance.load({ [locale]: {} });
      }

      // 2. 切换到目标语言
      changeCurrentLocale(locale);
    }
  }, [locale]);

  return <I18nContext value={{ locale, setLocale, fallbackLocale }}>{children}</I18nContext>;
}
